import React from 'react';
import { authFetch } from '../lib/authFetch';
import { t } from '../lib/i18n';
import Icon from '../components/Icon';

/**
 * ChangePasswordModal — modal dialog for changing password.
 *
 * Props:
 *   forceChange — if true, user cannot close the modal (must change password)
 *   onSuccess   — called after password is changed successfully
 *   onClose     — called when user closes the modal (only if !forceChange)
 */
export default function ChangePasswordModal({ onClose, onSuccess, forceChange }) {
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [done, setDone] = React.useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const r = await authFetch('/api/auth/change-password', {
                method: 'POST',
                body: JSON.stringify({ currentPassword, newPassword })
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || t('error_generic'));
            setDone(true);
            setTimeout(() => { onSuccess && onSuccess(); onClose && onClose(); }, 1500);
        } catch(err) { setError(err.message); }
        finally { setLoading(false); }
    };

    if (done) return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-lg w-full max-w-md mx-4 p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <Icon name="check-circle" className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-sm font-medium text-green-700">{t('auth_password_changed')}</p>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-lg w-full max-w-md mx-4">
                <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-[#1A1A2E] font-heading">{t('auth_change_password')}</h2>
                    {!forceChange && <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1A1A2E]"><Icon name="x" className="w-5 h-5" /></button>}
                </div>
                {forceChange && <div className="mx-6 mb-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700 border border-amber-100">{t('auth_must_change')}</div>}
                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                    {error && <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600 border border-red-100">{error}</div>}
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_current_password')}</label>
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required autoComplete="current-password" autoFocus
                            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_new_password_label')}</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} autoComplete="new-password"
                            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                        <p className="text-xs text-[#9CA3AF]">{t('auth_password_hint')}</p>
                    </div>
                    <button type="submit" disabled={loading} className="w-full py-2.5 bg-[#AA5D26] text-white rounded-lg text-sm font-semibold hover:bg-[#8B4513] transition-colors disabled:opacity-60">
                        {loading ? t('auth_changing') : t('auth_change_btn')}
                    </button>
                </form>
            </div>
        </div>
    );
}
